import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Location() {
  const titleAnim = useScrollAnimation({ threshold: 0.15 });
  const leftAnim = useScrollAnimation({ threshold: 0.1 });
  const rightAnim = useScrollAnimation({ threshold: 0.1 });

  const details = [
    {
      icon: 'ri-map-pin-2-fill',
      label: 'Address',
      value: 'Luisa St., Corner Mangga St., Juna\nSubdivision Ecoland, Matina, Davao City'
    },
    {
      icon: 'ri-time-fill',
      label: 'Business Hours',
      value: 'Mon \u2013 Sat: 8:00 AM \u2013 5:00 PM'
    },
    {
      icon: 'ri-phone-fill',
      label: 'Phone',
      value: '+63 (939) 939 8250'
    },
    {
      icon: 'ri-navigation-fill',
      label: 'Landmark',
      value: 'Near Ecoland Bus Terminal,\nMatina, Davao City'
    }
  ];

  return (
    <section id="location" className="py-14 lg:py-32 bg-[#0F0F0F]">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        {/* Section Header */}
        <div
          ref={titleAnim.ref}
          className={`text-center mb-10 lg:mb-16 transition-all duration-700 ${titleAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="text-[#FF6600] text-xs lg:text-sm font-semibold tracking-widest uppercase mb-2 lg:mb-3 block">
            Find Us
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-3 lg:mb-4">
            Our Location
          </h2>
          <div className="w-12 lg:w-16 h-1 bg-[#FF6600] mx-auto rounded-full"></div>
          <p className="text-white/60 mt-4 lg:mt-6 text-sm lg:text-lg max-w-xl mx-auto leading-relaxed">
            Visit us at our store in Davao City. We're easy to find and always ready to help with your home improvement needs.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-8 items-stretch">

          {/* Map — takes 3 columns */}
          <div
            ref={leftAnim.ref}
            className={`lg:col-span-3 transition-all duration-700 ${leftAnim.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div className="relative w-full h-full min-h-[280px] lg:min-h-[420px] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              {/* Decorative orange corner accent */}
              <div className="absolute top-0 right-0 w-10 lg:w-12 h-10 lg:h-12 bg-[#FF6600] z-10 rounded-bl-2xl flex items-center justify-center">
                <div className="w-4 lg:w-5 h-4 lg:h-5 flex items-center justify-center">
                  <i className="ri-map-pin-fill text-white text-base lg:text-lg"></i>
                </div>
              </div>
              <iframe
                title="Southdev Home Depot Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3959.0!2d125.5940067!3d7.0541539!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x32f97298f6b3e7db%3A0xde4f3d9e72385d8d!2sSouthdev%20Home%20Depot!5e0!3m2!1sen!2sph!4v1700000000000!5m2!1sen!2sph"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '280px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              ></iframe>
              <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#0F0F0F]/30 to-transparent pointer-events-none"></div>
            </div>
          </div>

          {/* Info Cards — takes 2 columns */}
          <div
            ref={rightAnim.ref}
            className={`lg:col-span-2 flex flex-col gap-3 lg:gap-5 transition-all duration-700 delay-200 ${rightAnim.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            {details.map((item, i) => (
              <div
                key={item.label}
                className={`flex items-start gap-3 lg:gap-5 bg-[#1A1A1A] border border-white/8 rounded-xl px-4 lg:px-6 py-3 lg:py-5 transition-all duration-500 hover:border-[#FF6600]/40 hover:bg-[#1F1F1F] group`}
                style={{ transitionDelay: `${200 + i * 100}ms` }}
              >
                <div className="w-9 lg:w-11 h-9 lg:h-11 flex items-center justify-center bg-[#FF6600]/10 rounded-lg group-hover:bg-[#FF6600]/20 transition-colors duration-200 flex-shrink-0">
                  <i className={`${item.icon} text-[#FF6600] text-lg lg:text-xl`}></i>
                </div>
                <div>
                  <div className="text-white/50 text-xs font-semibold tracking-widest uppercase mb-0.5 lg:mb-1">{item.label}</div>
                  <div className="text-white font-medium leading-relaxed whitespace-pre-line text-xs lg:text-sm">{item.value}</div>
                </div>
              </div>
            ))}

            {/* Get Directions Button */}
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=7.0541539,125.596587"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="mt-auto flex items-center justify-center gap-3 bg-[#FF6600] hover:bg-[#E55A00] text-white font-semibold py-3 lg:py-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap group text-sm lg:text-base"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-navigation-line text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </div>
              Get Directions
            </a>
          </div>

        </div>
      </div>
    </section>
  );
}
